import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AsyncCreatableSelect from 'react-select/async-creatable';
import Select from 'react-select';
import './LandingPage.css';
import contractorTypes from '../../Syn/ContractorTypes';
import Footer from '../../Footer/MainframeFooter';
import { trackEvent } from '../../../utils/analytics';
import { googleGeocode } from '../../../utils/googleMapsLoader';
import STATE_ABBR from '../../../utils/stateAbbreviations';
import { secureFetch } from '../../../utils/secureFetch';

const PLACES_CACHE_KEY = 'spiggl_places_cache';
const PLACES_CACHE_TTL = 24 * 60 * 60 * 1000;

let placesCache = {};
try {
  const stored = localStorage.getItem(PLACES_CACHE_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    const now = Date.now();
    Object.keys(parsed).forEach(key => {
      if (parsed[key].ts && now - parsed[key].ts < PLACES_CACHE_TTL) {
        placesCache[key] = parsed[key];
      }
    });
  }
} catch {}

const savePlacesCache = () => {
  try {
    localStorage.setItem(PLACES_CACHE_KEY, JSON.stringify(placesCache));
  } catch {}
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [selectedContractorType, setSelectedContractorType] = useState(null);
  const [location, setLocation] = useState('');
  const [errors, setErrors] = useState({ contractorType: false, location: false });
  const [touched, setTouched] = useState({ contractorType: false, location: false });
  const debounceRef = useRef(null);
  const pendingResolveRef = useRef(null);

  const handleSearch = () => {
    setTouched({ contractorType: true, location: true });

    if (!selectedContractorType || !location.trim()) {
      setErrors({
        contractorType: !selectedContractorType,
        location: !location.trim(),
      });
      return;
    }

    const type = encodeURIComponent(selectedContractorType.value);
    const loc = encodeURIComponent(location.trim());
    trackEvent('search', { metadata: { type: selectedContractorType.value, location: location.trim() } });
    navigate(`/search-results/${type}/${loc}`);
  };

  const handleContractorTypeChange = (selectedOption) => {
    setSelectedContractorType(selectedOption);
    setErrors((prev) => ({ ...prev, contractorType: false }));
    setTouched((prev) => ({ ...prev, contractorType: true }));
  };

  const getStoredCoords = () => {
    try {
      const lastLoc = localStorage.getItem('spiggl_last_search_location');
      if (!lastLoc) return null;
      const cache = JSON.parse(localStorage.getItem('spiggl_geocode_cache') || '{}');
      const entry = cache[lastLoc];
      if (entry?.latitude && entry?.longitude) return { lat: entry.latitude, lon: entry.longitude };
    } catch {}
    return null;
  };

  const parsePhotonResults = (data) => {
    return (data.features || [])
      .filter(f => {
        const p = f.properties || {};
        return p.countrycode === 'US' && p.osm_key === 'place';
      })
      .map(f => {
        const p = f.properties || {};
        const rawCity = p.name || '';
        const city = rawCity.replace(/ Township$/i, '');
        const state = STATE_ABBR[p.state] || p.state || '';
        const label = state ? `${city}, ${state}` : city;
        return { label, value: label };
      })
      .filter((v, i, a) => v.label && a.findIndex(t => t.value === v.value) === i);
  };

  const fetchPlaces = async (inputValue) => {
    if (placesCache[inputValue]?.results) return placesCache[inputValue].results;
    try {
      const baseUrl = `https://photon.komoot.io/api/?q=${encodeURIComponent(inputValue)}&lang=en`;
      const coords = getStoredCoords();

      let results;
      if (coords) {
        const [biasedRes, globalRes] = await Promise.all([
          secureFetch(`${baseUrl}&limit=15&lat=${coords.lat}&lon=${coords.lon}&location_bias_scale=0.1`, { retries: 0, timeout: 8000 }).then(r => r.ok ? r.json() : { features: [] }),
          secureFetch(`${baseUrl}&limit=30`, { retries: 0, timeout: 8000 }).then(r => r.ok ? r.json() : { features: [] }),
        ]);
        const local = parsePhotonResults(biasedRes);
        const global = parsePhotonResults(globalRes);
        const seen = new Set(local.map(r => r.value));
        results = [...local, ...global.filter(r => !seen.has(r.value))].slice(0, 10);
      } else {
        const res = await secureFetch(`${baseUrl}&limit=50`, { retries: 0, timeout: 8000 });
        if (!res.ok) return [];
        const data = await res.json();
        results = parsePhotonResults(data).slice(0, 10);
      }

      placesCache[inputValue] = { results, ts: Date.now() };
      savePlacesCache();
      return results;
    } catch {
      return [];
    }
  };

  const loadOptions = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) return Promise.resolve([]);
    if (pendingResolveRef.current) {
      pendingResolveRef.current([]);
      pendingResolveRef.current = null;
    }
    clearTimeout(debounceRef.current);
    return new Promise((resolve) => {
      pendingResolveRef.current = resolve;
      debounceRef.current = setTimeout(async () => {
        const results = await fetchPlaces(inputValue);
        pendingResolveRef.current = null;
        resolve(results);
      }, 300);
    });
  }, []);

  const handleLocationInputChange = (selectedOption) => {
    setLocation(selectedOption ? selectedOption.value : '');
    setErrors((prev) => ({ ...prev, location: false, locationError: undefined }));
    setTouched((prev) => ({ ...prev, location: true }));
  };

  const handleLocationClick = () => {
    if (!navigator.geolocation) {
      setErrors((prev) => ({
        ...prev,
        location: true,
        locationError: 'Location services are not supported by your browser. Please enter location manually.',
      }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const results = await googleGeocode({ latlng: `${latitude},${longitude}` });
          if (results && results.length > 0) {
            let city = '';
            let state = '';
            for (const component of results[0].address_components) {
              if (component.types.includes('locality')) city = component.long_name;
              if (component.types.includes('administrative_area_level_1')) state = component.short_name;
            }
            const locationString = city && state
              ? `${city}, ${state}`
              : results[0].formatted_address.split(',').slice(0, 2).join(',').trim();
            setLocation(locationString);
            setErrors((prev) => ({ ...prev, location: false, locationError: undefined }));
            setTouched((prev) => ({ ...prev, location: true }));
            trackEvent('location_auto_detected', { metadata: { location: locationString } });
          }
        } catch {
          setErrors((prev) => ({
            ...prev,
            location: true,
            locationError: "Couldn't get your location. Turn on location access or type a zip code.",
          }));
        }
      },
      () => {
        setErrors((prev) => ({
          ...prev,
          location: true,
          locationError: "Couldn't get your location. Turn on location access or type a zip code.",
        }));
      }
    );
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 'var(--radius-xl, 15px)',
      border: `2px solid ${state.selectProps.className?.includes('input-error') ? 'var(--color-error, #d3211b)' : state.isFocused ? 'var(--color-primary)' : 'var(--color-primary)'}`,
      padding: '0.4rem',
      fontFamily: 'var(--font-primary, "Poppins", sans-serif)',
      fontSize: 'var(--text-lg, 1.2rem)',
      boxShadow: state.isFocused ? 'var(--shadow-md)' : 'var(--shadow-sm)',
    }),
    placeholder: (provided) => ({
      ...provided,
      textAlign: 'left',
      color: 'var(--color-text-secondary, #6c757d)',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--color-primary)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'var(--color-primary)'
        : state.isFocused
          ? 'var(--color-tertiary)'
          : 'white',
      color: state.isSelected ? 'white' : 'var(--color-primary)',
    }),
  };

  return (
    <div className="landing-page">
      <section className="landing-hero">
        <h1 className="landing-hero-headline">
          Find local contractors and see how people rated them.
        </h1>
        <div className="landing-search-container">
          <div className="landing-search-group">
            <div className="landing-select-group">
              <Select
                classNamePrefix="main-react-select"
                className={`type-react-select ${touched.contractorType && errors.contractorType ? 'input-error' : ''}`}
                value={selectedContractorType}
                onChange={handleContractorTypeChange}
                options={contractorTypes}
                placeholder="Contractor Type"
                isClearable
                onBlur={() => setTouched((prev) => ({ ...prev, contractorType: true }))}
                styles={selectStyles}
              />
              <div className="landing-location-input">
                <AsyncCreatableSelect
                  classNamePrefix="main-react-select"
                  className={`location-react-select ${touched.location && errors.location ? 'input-error' : ''}`}
                  value={location ? { label: location, value: location } : null}
                  onChange={handleLocationInputChange}
                  loadOptions={loadOptions}
                  placeholder="Zip code or city, state"
                  isClearable
                  onBlur={() => setTouched((prev) => ({ ...prev, location: true }))}
                  styles={selectStyles}
                  formatCreateLabel={(val) => val}
                  createOptionPosition="first"
                  allowCreateWhileLoading
                />
                <button
                  className="landing-location-icon-btn"
                  onClick={handleLocationClick}
                  aria-label="Use my current location"
                  type="button"
                >
                  <img
                    src="/icons/location.png"
                    alt=""
                    className="landing-location-icon"
                  />
                </button>
              </div>
            </div>
            <button className="landing-search-button" onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
      </section>

      <section className="landing-how-it-works">
        <h2 className="landing-section-title">How It Works</h2>
        <div className="landing-steps-grid">
          <div className="landing-step">
            <div className="landing-step-title">Find</div>
            <p className="landing-step-description">Search by trade and town to see contractors near you and their Spiggl Scores.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-title">Message</div>
            <p className="landing-step-description">Ask questions and get a quote without leaving Spiggl.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-title">Schedule</div>
            <p className="landing-step-description">Pick a time that works for both of you.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-title">Pay</div>
            <p className="landing-step-description">Pay through Spiggl once the job is done.</p>
          </div>
        </div>
      </section>

      <section className="landing-score-section">
        <div className="landing-score-content">
          <h2 className="landing-score-title">The Spiggl Score</h2>
          <p className="landing-score-description">
            Every contractor gets a score from -10 to +10, calculated from reviews left by Spiggl users.
            Recent reviews count more than old ones, and contractors can't pay to move up.
          </p>
          <div className="landing-score-bar">
            <span className="landing-score-label">-10</span>
            <div className="landing-score-track">
              <div className="landing-score-fill"></div>
            </div>
            <span className="landing-score-label">+10</span>
          </div>
        </div>
      </section>

      <section className="landing-cta-section">
        <div className="landing-cta-grid">
          <div className="landing-cta-card">
            <h3 className="landing-cta-card-title">For Homeowners</h3>
            <p className="landing-cta-card-description">
              Compare scores and reviews before you let anyone into your house.
            </p>
            <button
              className="landing-cta-button primary"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Search Contractors
            </button>
          </div>
          <div className="landing-cta-card">
            <h3 className="landing-cta-card-title">For Contractors</h3>
            <p className="landing-cta-card-description">
              Listing your business is free. Do good work and your score will show it.
            </p>
            <button
              className="landing-cta-button secondary"
              onClick={() => navigate('/create-profile?tab=company')}
            >
              List Your Business
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
